import { useEffect, useState } from "react";
import {
  JOB_TYPE_LABELS,
  type Job,
  type Driver,
  type Truck,
} from "@eagledispatch/shared";
import { apiFetch } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import {
  resolveDriverLabel,
  resolveDriverLabelDash,
  resolveTruckLabel,
} from "../../lib/job-card-labels";
import Button from "../../components/Button";
import Card from "../../components/Card";
import StatusTag from "../../components/StatusTag";

export type OfficeRoute =
  | { name: "home" }
  | { name: "map" }
  | { name: "new-job" }
  | { name: "active-job"; id: string }
  | { name: "finished-job"; id: string }
  | { name: "reviewed-job"; id: string }
  | { name: "export" }
  | { name: "timeclock" }
  | { name: "admin" };

function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function OfficeHome({
  onNavigate,
}: {
  onNavigate: (route: OfficeRoute) => void;
}) {
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [activeTotal, setActiveTotal] = useState(0);
  const [activeLoading, setActiveLoading] = useState(true);

  const [finishedJobs, setFinishedJobs] = useState<Job[]>([]);
  const [finishedTotal, setFinishedTotal] = useState(0);
  const [finishedLoading, setFinishedLoading] = useState(true);

  const [reviewedJobs, setReviewedJobs] = useState<Job[]>([]);
  const [reviewedTotal, setReviewedTotal] = useState(0);
  const [reviewedLoading, setReviewedLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  // The jobs list returns FLAT rows (driverId/truckId only — the nested
  // `driver`/`truck` relations are attached only by the detail routes), so we
  // fetch the two rosters once and resolve display labels from them. `null`
  // means "not loaded yet" (cards render "…", never a transient false
  // "Unassigned"); an EMPTY Map means the fetch FAILED terminally (the miss
  // tier renders) — a failed roster must not read as "still loading" forever.
  const [driverNames, setDriverNames] = useState<Map<string, string> | null>(null);
  const [truckUnits, setTruckUnits] = useState<Map<string, number> | null>(null);

  // OFFICE-HOME-TABS — one list at a time (PM directive 2026-09-17).
  const [jobTab, setJobTab] = useState<"active" | "review-queue" | "reviewed">("active");

  // Rosters: DISPATCHER+OFFICE-legal list endpoints (drivers.ts, reference.ts).
  // `includeInactive=true` on both: an Admin-deactivated-but-real driver/truck
  // still exists and can still be on jobs — an activity-filtered roster would
  // turn its labels into a false "Unknown driver" / "Truck ?" the moment a
  // Refresh refetches the job lists without the rosters. The "Unknown" tier is
  // reserved for ids the roster genuinely cannot resolve.
  useEffect(() => {
    apiFetch<Driver[]>("/api/v1/drivers?includeInactive=true")
      .then((drivers) => {
        const map = new Map<string, string>();
        for (const d of drivers) map.set(d.id, d.name);
        setDriverNames(map);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        // Terminal failure: empty Map (≠ null "pending") so cards render the
        // miss tier instead of "…" forever.
        setDriverNames(new Map());
      });

    apiFetch<Truck[]>("/api/v1/trucks?includeInactive=true")
      .then((trucks) => {
        const map = new Map<string, number>();
        for (const t of trucks) map.set(t.id, t.unitNumber);
        setTruckUnits(map);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        // Terminal failure: empty Map (≠ null "pending") so cards render the
        // miss tier instead of "…" forever.
        setTruckUnits(new Map());
      });
  }, []);

  const loadData = () => {
    setError(null);
    setActiveLoading(true);
    setFinishedLoading(true);
    setReviewedLoading(true);

    apiFetch<{ jobs: Job[]; total: number }>("/api/v1/jobs?state=NEW,IN_PROGRESS,PAUSED&limit=50")
      .then((res) => {
        setActiveJobs(res.jobs);
        setActiveTotal(res.total);
        setActiveLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setActiveLoading(false);
      });

    apiFetch<{ jobs: Job[]; total: number }>("/api/v1/jobs?state=FINISHED&limit=50")
      .then((res) => {
        setFinishedJobs(res.jobs);
        setFinishedTotal(res.total);
        setFinishedLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setFinishedLoading(false);
      });

    apiFetch<{ jobs: Job[]; total: number }>("/api/v1/jobs?state=REVIEWED&limit=50")
      .then((res) => {
        setReviewedJobs(res.jobs);
        setReviewedTotal(res.total);
        setReviewedLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setReviewedLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <main className="surface office-surface">
      <header className="surface-header">
        <div>
          <h1>Office Dashboard</h1>
          <p className="sub">Job pipeline &amp; finalized accounting records</p>
        </div>
      </header>

      {error && (
        <div className="banner banner-danger" style={{ marginBottom: "16px" }}>
          {error}
        </div>
      )}

      {/* Unit #19: big green NEW JOB button ABOVE the Review Queue. */}
      <div style={{ marginBottom: "20px" }}>
        <button
          type="button"
          className="office-new-job-cta"
          onClick={() => onNavigate({ name: "new-job" })}
        >
          <span className="office-new-job-cta-title">+ NEW UNASSIGNED JOB</span>
          <span className="office-new-job-cta-hint">
            Office creates — the dispatcher will assign a driver &amp; truck.
          </span>
        </button>
      </div>

      {/* OFFICE-HOME-TABS — tab bar styled to match .office-new-job-cta width. */}
      <div className="office-tabs">
        <div className="office-tabs-bar" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={jobTab === "active"}
            className={jobTab === "active" ? "office-tab is-active" : "office-tab"}
            onClick={() => setJobTab("active")}
          >
            Active Jobs <span className="badge-count badge-blue">{activeTotal}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={jobTab === "review-queue"}
            className={jobTab === "review-queue" ? "office-tab is-active" : "office-tab"}
            onClick={() => setJobTab("review-queue")}
          >
            Review Queue <span className="badge-count">{finishedTotal}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={jobTab === "reviewed"}
            className={jobTab === "reviewed" ? "office-tab is-active" : "office-tab"}
            onClick={() => setJobTab("reviewed")}
          >
            Reviewed Jobs <span className="badge-count badge-reviewed">{reviewedTotal}</span>
          </button>
        </div>

        {/* one panel at a time — the three former office-col sections, verbatim */}
        {jobTab === "active" && (
        /* Section 1: Active Jobs (NEW & IN_PROGRESS) - Unit #20 */
        <div className="office-col">
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h2>Active Jobs</h2>
                <span className="badge-count badge-blue" title="Active jobs">
                  {activeTotal}
                </span>
              </div>
              <Button variant="ghost" onClick={loadData}>
                ↻ Refresh
              </Button>
            </div>

            <p className="muted" style={{ fontSize: "13px", marginBottom: "12px" }}>
              Jobs currently active, paused, or awaiting dispatch. Click to pause, cancel, or reassign.
            </p>

            {activeLoading ? (
              <p className="muted">Loading active jobs…</p>
            ) : activeJobs.length === 0 ? (
              <div className="empty-state-box">
                <p className="muted">No active jobs in the queue.</p>
              </div>
            ) : (
              <div className="job-list-stack">
                {activeJobs.map((j) => {
                  const snap = (j.customerSnapshot ?? {}) as {
                    customer?: Record<string, unknown>;
                    property?: Record<string, unknown>;
                  };
                  const company = (snap.customer?.companyName as string) || "Unknown Customer";
                  const site = (snap.property?.siteName as string) || "";

                  return (
                    <div
                      key={j.id}
                      className="office-job-card"
                      onClick={() => onNavigate({ name: "active-job", id: j.id })}
                    >
                      <div className="office-job-card-top">
                        <span className="job-number">{j.jobNumber}</span>
                        <StatusTag state={j.state} />
                      </div>
                      <div className="office-job-card-main">
                        <strong>{company}</strong>
                        {site ? <span className="muted"> · {site}</span> : null}
                      </div>
                      <div className="office-job-card-meta">
                        <span>{JOB_TYPE_LABELS[j.jobType]}</span>
                        <span>·</span>
                        <span>{resolveDriverLabel(j.driverId, driverNames)}</span>
                        <span>·</span>
                        <span>{resolveTruckLabel(j.truckId, truckUnits)}</span>
                      </div>
                      {j.loadDecision && (
                        <div
                          className={j.loadDecision === "LANDFILL" ? "tag tag-blue" : "tag tag-green"}
                          style={{ alignSelf: "flex-start", marginTop: "4px", fontSize: "11px" }}
                        >
                          {j.loadDecision === "LANDFILL" ? "LANDFILL" : "FACILITY METAL"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
        )}

        {jobTab === "review-queue" && (
        /* Section 2: Review Queue (FINISHED) */
        <div className="office-col">
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h2>Review Queue</h2>
                <span className="badge-count" title="Needs review">
                  {finishedTotal}
                </span>
              </div>
              <Button variant="ghost" onClick={loadData}>
                ↻ Refresh
              </Button>
            </div>

            <p className="muted" style={{ fontSize: "13px", marginBottom: "12px" }}>
              Jobs completed in the field awaiting office weight-ticket review &amp; billing entry.
            </p>

            {finishedLoading ? (
              <p className="muted">Loading review queue…</p>
            ) : finishedJobs.length === 0 ? (
              <div className="empty-state-box">
                <p>✅ <strong>All caught up!</strong></p>
                <p className="muted" style={{ fontSize: "13px" }}>No jobs currently need office review.</p>
              </div>
            ) : (
              <div className="job-list-stack">
                {finishedJobs.map((j) => {
                  const snap = (j.customerSnapshot ?? {}) as {
                    customer?: Record<string, unknown>;
                    property?: Record<string, unknown>;
                  };
                  const company = (snap.customer?.companyName as string) || "Unknown Customer";
                  const site = (snap.property?.siteName as string) || "";

                  return (
                    <div
                      key={j.id}
                      className="office-job-card"
                      onClick={() => onNavigate({ name: "finished-job", id: j.id })}
                    >
                      <div className="office-job-card-top">
                        <span className="job-number">{j.jobNumber}</span>
                        <StatusTag state={j.state} />
                      </div>
                      <div className="office-job-card-main">
                        <strong>{company}</strong>
                        {site ? <span className="muted"> · {site}</span> : null}
                      </div>
                      <div className="office-job-card-meta">
                        <span>{JOB_TYPE_LABELS[j.jobType]}</span>
                        <span>·</span>
                        <span>Driver: {resolveDriverLabelDash(j.driverId, driverNames)}</span>
                        <span>·</span>
                        <span>Finished: {formatDateTime(j.finishAt)}</span>
                      </div>
                      {j.photoSource === "GALLERY" && (
                        <div className="tag tag-red" style={{ alignSelf: "flex-start", marginTop: "4px" }}>
                          ⚠️ GALLERY PHOTO
                        </div>
                      )}
                      {j.loadDecision && (
                        <div
                          className={j.loadDecision === "LANDFILL" ? "tag tag-blue" : "tag tag-green"}
                          style={{ alignSelf: "flex-start", marginTop: "4px", fontSize: "11px" }}
                        >
                          {j.loadDecision === "LANDFILL" ? "LANDFILL" : "FACILITY METAL"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
        )}

        {jobTab === "reviewed" && (
        /* Section 3: Reviewed Jobs */
        <div className="office-col">
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h2>Reviewed Jobs</h2>
                <span className="badge-count badge-reviewed" title="Reviewed jobs">
                  {reviewedTotal}
                </span>
              </div>
              <Button variant="ghost" onClick={loadData}>
                ↻ Refresh
              </Button>
            </div>

            <p className="muted" style={{ fontSize: "13px", marginBottom: "12px" }}>
              Jobs reviewed and export-ready for QuickBooks Online. Click any job to view audit history or correct amounts.
            </p>

            {reviewedLoading ? (
              <p className="muted">Loading reviewed jobs…</p>
            ) : reviewedJobs.length === 0 ? (
              <div className="empty-state-box">
                <p className="muted">No reviewed jobs recorded yet.</p>
              </div>
            ) : (
              <div className="job-list-stack">
                {reviewedJobs.map((j) => {
                  const snap = (j.customerSnapshot ?? {}) as {
                    customer?: Record<string, unknown>;
                    property?: Record<string, unknown>;
                  };
                  const company = (snap.customer?.companyName as string) || "Unknown Customer";

                  return (
                    <div
                      key={j.id}
                      className="office-job-card"
                      onClick={() => onNavigate({ name: "reviewed-job", id: j.id })}
                    >
                      <div className="office-job-card-top">
                        <span className="job-number">{j.jobNumber}</span>
                        <span className="money-tag">{money(j.billableAmountCents)}</span>
                      </div>
                      <div className="office-job-card-main">
                        <strong>{company}</strong>
                      </div>
                      <div className="office-job-card-meta">
                        <span>{JOB_TYPE_LABELS[j.jobType]}</span>
                        {j.weightTons !== null ? (
                          <>
                            <span>·</span>
                            <span>{j.weightTons} tons</span>
                          </>
                        ) : null}
                        <span>·</span>
                        <span>{formatDateTime(j.finishAt)}</span>
                      </div>
                      {j.loadDecision && (
                        <div
                          className={j.loadDecision === "LANDFILL" ? "tag tag-blue" : "tag tag-green"}
                          style={{ alignSelf: "flex-start", marginTop: "4px", fontSize: "11px" }}
                        >
                          {j.loadDecision === "LANDFILL" ? "LANDFILL" : "FACILITY METAL"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
        )}
      </div>
    </main>
  );
}
